/**
 * FOUND-1 · canonical entry / label / capability-home registry (§3.7.10).
 * Topbar, Search palette, Needs You, and chrome CTAs must resolve here.
 * Soft≠Live: product labels only — no Page / endpoint / plane in operator copy.
 * Response and Prepare paths are listed for typed handoff from other primaries.
 * Does not import ui.js (avoids load cycles) — callers pass go().
 */

/** Primary topbar seats (product + lab). */
/** Audience: tenant = every signed-in seat; platform = SA/OTA; hidden_lab = not topbar. */
export const PRIMARY_NAV = Object.freeze([
  { id: "ops", label: "Operations", path: "/ops", audience: "tenant" },
  { id: "cameras", label: "Cameras", path: "/cameras/live", audience: "tenant" },
  { id: "response", label: "Response", path: "/response/board", audience: "tenant" },
  { id: "dispatch", label: "Resources", path: "/dispatch/people", audience: "tenant" },
  { id: "places", label: "Places", path: "/places/zones", audience: "tenant" },
  { id: "prepare", label: "Prepare", path: "/prepare/readiness", audience: "tenant" },
  { id: "machine", label: "Machine", path: "/machine", audience: "tenant" },
  { id: "skills", label: "Skills", path: "/skills/library", audience: "hardware" },
  { id: "activate", label: "Activate", path: "/activate", audience: "tenant" },
  { id: "automations", label: "Automations", path: "/automations", audience: "tenant" },
  { id: "ota", label: "OTA", path: "/ota", ota: true, audience: "platform" },
  { id: "settings", label: "Settings", path: "/settings/profile", audience: "tenant" },
  { id: "sa", label: "Platform", path: "/sa", sa: true, audience: "platform" },
  { id: "fleet", label: "Fleet", path: "/fleet/firmware", lab: true, audience: "hidden_lab" },
  { id: "edge", label: "Edge", path: "/edge/estate", lab: true, audience: "hidden_lab" },
  { id: "control", label: "Control", path: "/control/shares", lab: true, audience: "hidden_lab" },
]);

/**
 * SURF-CAP-HOME — one capability noun → one owning desk.
 * Bare "Capabilities" / "Group" / "Zone" / "Rules" must not appear without owner.
 */
export const CAP_HOME = Object.freeze({
  machine_capability_packs: {
    id: "machine_capability_packs",
    label: "Machine Capability Packs",
    path: "/skills/library",
    primary: "skills",
  },
  staff_permissions: {
    id: "staff_permissions",
    label: "Staff Permissions",
    path: "/sa/caps",
    primary: "sa",
  },
  ota_capability_rollout: {
    id: "ota_capability_rollout",
    label: "Skills Rollout",
    path: "/skills/waves",
    primary: "skills",
  },
  machine_group_devices: {
    id: "machine_group_devices",
    label: "Machine Group · Devices",
    path: "/machine/groups",
    primary: "machine",
  },
  group_grants: {
    id: "group_grants",
    label: "Group Grants",
    path: "/settings/delegations",
    primary: "settings",
  },
  areas: {
    id: "areas",
    label: "Zones",
    path: "/places/zones",
    primary: "places",
  },
  automation: {
    id: "automation",
    label: "Automations",
    path: "/automations",
    primary: "automations",
  },
  trigger_policy: {
    id: "trigger_policy",
    label: "Trigger policy",
    path: "/settings/trigger",
    primary: "settings",
  },
  ai_policy: {
    id: "ai_policy",
    label: "AI policy & charter",
    path: "/settings/ai",
    primary: "settings",
  },
  sites: {
    id: "sites",
    label: "Sites",
    path: "/places/sites",
    primary: "places",
  },
  machine_health: {
    id: "machine_health",
    label: "Machine health focus",
    path: "/machine?view=health",
    primary: "machine",
  },
  needs_you: {
    id: "needs_you",
    label: "Needs You · Mission Room",
    path: "/response/cases",
    primary: "response",
  },
  edge_lab: {
    id: "edge_lab",
    label: "Edge Lab",
    path: "/sa/edge-lab",
    primary: "sa",
  },
});

/** Search / launcher screens — labels are product language only. */
export const PALETTE_SCREENS = Object.freeze([
  { k: "Register a machine", hint: "activate", path: "/activate" },
  { k: "Node interfaces · board", hint: "activate", path: "/activate/interfaces" },
  { k: "Activate capability steps", hint: "activate", path: "/activate/capabilities" },
  { k: "Survey a position", hint: "activate", path: "/activate/position" },
  { k: "People book", hint: "resources", path: "/dispatch/people" },
  { k: "Site membership · work charter", hint: "places", path: "/places/sites" },
  { k: "Zones & groups · Places", hint: "places", path: "/places/zones" },
  { k: "Register ground robots", hint: "resources", path: "/dispatch/robots" },
  { k: "Drone register", hint: "resources", path: "/dispatch/drones" },
  { k: "Skills library", hint: "skills", path: "/skills/library" },
  { k: "Assign Skills", hint: "skills", path: "/skills/assign" },
  { k: "Skills rollout waves", hint: "skills", path: "/skills/waves" },
  { k: "Automations", hint: "automations", path: "/automations" },
  { k: "OTA dual rail", hint: "ota", path: "/ota" },
  { k: "Ops readiness · ontology", hint: "ops", path: "/ops/resources" },
  { k: "Machine list", hint: "machine", path: "/machine" },
  { k: "Machine Group · Devices", hint: "machine", path: "/machine/groups" },
  { k: "Machine Capability Packs", hint: "skills", path: "/skills/library" },
  { k: "Areas focus", hint: "ops", path: "/ops/zones" },
  { k: "Edge Lab", hint: "platform", path: "/sa/edge-lab" },
  { k: "Needs You · Mission Room", hint: "response", path: "/response/cases" },
  { k: "Ops readiness · robots", hint: "ops", path: "/ops/resources?type=robot" },
  { k: "External robots · command", hint: "response", path: "/response/robots" },
  { k: "Practice drill", hint: "prepare", path: "/prepare/drill" },
  { k: "Before tonight", hint: "prepare", path: "/prepare/readiness" },
  { k: "Response plans", hint: "prepare", path: "/prepare/playbook" },
  { k: "Ops readiness · equipment", hint: "ops", path: "/ops/resources?type=equipment" },
  { k: "Cameras Live", hint: "cameras", path: "/cameras/live" },
  { k: "Cameras Retained", hint: "cameras", path: "/cameras/retained" },
  { k: "Camera capture & bind", hint: "cameras", path: "/cameras/manage" },
  { k: "Firmware & campaigns", hint: "ota", path: "/ota" },
  { k: "Machine health focus", hint: "machine", path: "/machine?view=health" },
  { k: "Why is it offline", hint: "machine", path: "/machine?view=health" },
  { k: "Machine AI actions", hint: "machine", path: "/machine" },
  { k: "Links & siblings", hint: "edge", path: "/edge/link" },
  { k: "Group Grants", hint: "settings", path: "/settings/delegations" },
  { k: "Audit export", hint: "settings", path: "/settings/audit" },
  { k: "Billing", hint: "settings", path: "/settings/billing" },
  { k: "AI team tools", hint: "settings", path: "/settings/ai", seat: "tenant_user" },
  { k: "Platform Skills", hint: "platform", path: "/sa/ai-tools" },
  { k: "Staff Permissions", hint: "platform", path: "/sa/caps" },
  { k: "Edge Workbench", hint: "edge", path: "/edge/bench" },
  { k: "Notifications", hint: "settings", path: "/settings/notify" },
  { k: "Alarm behaviour", hint: "settings", path: "/settings/alarm" },
  { k: "Trigger policy", hint: "settings", path: "/settings/trigger" },
  { k: "Ops Alarms", hint: "ops", path: "/ops/alarms" },
  { k: "Platform Audit", hint: "platform", path: "/sa/audit" },
  { k: "AI policy & charter", hint: "settings", path: "/settings/ai", seat: "tenant_admin" },
  { k: "Platform control", hint: "platform", path: "/sa/caps" },
  { k: "Tunnel & live video path", hint: "edge", path: "/edge/tunnel" },
  { k: "Areas on Fleet map", hint: "fleet", path: "/fleet/zones" },
  { k: "Sign out", hint: "auth", path: "/auth/signout" },
  { k: "Sign in", hint: "auth", path: "/auth/login" },
  { k: "Create an account", hint: "auth", path: "/auth/signup" },
  { k: "Reset password", hint: "auth", path: "/auth/forgot" },
]);

export function capHomeOf(id) {
  return CAP_HOME[String(id || "").trim()] || null;
}

/** Palette rows from registry — caller supplies go(). SA paths filtered by seat. */
export function paletteScreenItems(goFn, me) {
  const run = typeof goFn === "function" ? goFn : () => {};
  const role = String((me && me.role) || "");
  const saOk =
    role === "superadmin" ||
    role === "sa_staff" ||
    (typeof me === "object" &&
      me &&
      Array.isArray(me.caps) &&
      (me.caps.includes("sa_full_access") || me.caps.includes("sa_audit_read")));
  return PALETTE_SCREENS.filter((row) => {
    const p = String(row.path || "");
    const hint = String(row.hint || "");
    if (p === "/sa" || p.startsWith("/sa/")) return saOk;
    if (hint === "ota" || p === "/ota" || p.startsWith("/ota/") || p === "/settings/ota") return saOk;
    if (hint === "platform") return saOk;
    if (hint === "edge" || hint === "fleet" || p.startsWith("/edge/") || p.startsWith("/fleet/") || p.startsWith("/control/")) {
      return saOk;
    }
    if (p === "/activate" || p.startsWith("/activate/")) {
      const r = String(role || "");
      if (r === "superadmin" || r === "admin") return true;
      if (r === "sa_staff") {
        const caps = Array.isArray(me && me.caps) ? me.caps : [];
        return caps.includes("sa_full_access") || caps.includes("sa_device_admin");
      }
      const pol = (me && me.policy && typeof me.policy === "object") ? me.policy : {};
      return Number(pol.can_claim_device) === 1;
    }
    if (hint === "skills" || p === "/skills" || p.startsWith("/skills/")) {
      if (role === "superadmin" || role === "admin") return true;
      const caps = Array.isArray(me && me.caps) ? me.caps : [];
      return caps.includes("sa_full_access") || caps.includes("sa_ota_admin");
    }
    if (hint === "prepare" || p === "/prepare" || p.startsWith("/prepare/")
      || hint === "automations" || p === "/automations" || p.startsWith("/automations/")) {
      if (role === "superadmin" || role === "admin") return true;
      if (role === "sa_staff") {
        const caps = Array.isArray(me && me.caps) ? me.caps : [];
        return caps.includes("sa_full_access") || caps.includes("sa_ota_admin") || caps.includes("sa_device_admin");
      }
      return false;
    }
    if (p === "/settings/ai") {
      const seat = String(row.seat || "tenant_admin");
      if (role === "user") return seat === "tenant_user";
      if (role === "admin") return seat === "tenant_user" || seat === "tenant_admin";
      if (role === "superadmin" || role === "sa_staff") return true;
      return false;
    }
    if (
      p === "/settings/delegations"
      || p === "/settings/audit"
      || p === "/settings/billing"
      || p === "/settings/sites"
      || p === "/settings/integrate"
    ) {
      return role === "superadmin" || role === "admin" || role === "sa_staff";
    }
    return true;
  }).map((row) => ({
    k: row.k,
    hint: row.hint,
    run: () => run(row.path),
  }));
}

/** Assert unique capability homes (contract / debug). */
export function assertCapHomeUnique() {
  const seen = new Map();
  for (const row of Object.values(CAP_HOME)) {
    const prev = seen.get(row.path);
    if (prev && prev !== row.id) {
      throw new Error("CAP_HOME collision: " + row.path + " owned by " + prev + " and " + row.id);
    }
    seen.set(row.path, row.id);
  }
  return true;
}
